import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {EsimPackage} from '@guestapp/sdk';
import {Counter, CounterSize} from 'components/common/Counter';
import {Fragment} from 'react/jsx-runtime';
import cn from 'clsx';
import {FormTypes} from './types';
import {Button} from '@chekin/guestapp-ui-kit';

const getPrice = (esimPackage: EsimPackage, count: number = 1) => {
  return (Number(esimPackage.package_price) * count).toFixed(2);
};

const getTotalPrice = (esimPackages: EsimPackage[]) => (values: FormTypes) => {
  return esimPackages.reduce((acc, esimPackage) => {
    if (values[esimPackage.package_id]?.checked) {
      return (
        acc +
        Number(esimPackage.package_price) * (values[esimPackage.package_id]?.count || 1)
      );
    }
    return acc;
  }, 0);
};

const hasItems = (esimPackages: EsimPackage[]) => (values: FormTypes) => {
  return esimPackages.some(esimPackage => values[esimPackage.package_id]?.checked);
};

type Props = {
  isLoading: boolean;
  className?: string;
  esimPackages: EsimPackage[];
  formId: string;
};
export const TotalToPay = ({className, esimPackages, formId, isLoading}: Props) => {
  const {t} = useTranslation();
  const {control, setValue, unregister} = useFormContext();
  const values = useWatch({control});

  const hancldeChange =
    (id: string, onChange: (value: number) => void) => (value: number) => {
      if (value <= 0) {
        setValue(`${id}.checked`, false);
        unregister(`${id}.count`);
      } else {
        onChange(value);
      }
    };

  const isNotEmpty = hasItems(esimPackages)(values);

  return (
    <div
      className={cn(
        className,
        'flex flex-col gap-10 p-10 shadow-md rounded-2xl self-start bg-[var(--navigation-background)]',
      )}
    >
      <div className="flex flex-col gap-6">
        <div>{t('esim.choose_plan.selected_plans')}</div>
        {isNotEmpty ? (
          <div className="w-full grid gap-x-2 gap-y-4 grid-cols-3 [&>*:nth-child(3n+2)]:justify-self-center [&>*:nth-child(3n+3)]:justify-self-end">
            {esimPackages.map(
              esimPackage =>
                values[esimPackage.package_id]?.checked && (
                  <Fragment key={esimPackage.package_id}>
                    <div>{esimPackage.package_title}</div>

                    <Controller
                      name={`${esimPackage.package_id}.count`}
                      control={control}
                      render={({field}) => (
                        <Counter
                          value={field.value ?? 1}
                          onChange={hancldeChange(esimPackage.package_id, field.onChange)}
                          size={CounterSize.S}
                        />
                      )}
                    />

                    <div>
                      {getPrice(esimPackage, values[esimPackage.package_id]?.count)}
                    </div>
                  </Fragment>
                ),
            )}
          </div>
        ) : (
          <div>{t('esim.choose_plan.no_items_selected')}</div>
        )}
        <div className="flex w-full justify-between gap-2">
          <div>{t('esim.choose_plan.total_to_pay')}</div>
          <div>{getTotalPrice(esimPackages)(values).toFixed(2)}</div>
        </div>
      </div>
      <Button form={formId} type="submit" disabled={!isNotEmpty || isLoading}>
        {t('esim.choose_plan.buy_now')}
      </Button>
    </div>
  );
};
